import React from 'react';
import ReactDOM from 'react-dom/client';
import posthog from 'posthog-js';
import App from './App';
import { applyAccent } from './utils/accent';
import { installErrorReporting } from './utils/errorReport';

// Anonymous usage analytics (PostHog EU) — live site only, never in dev.
// Session recording stays off: a calendar shows personal data on screen.
if (/(^|\.)memocal\.app$/.test(window.location.hostname)) {
  posthog.init('phc_u9HPkn3h4UFpkrkXwp3sWRaLcqig4WvfRVLm3UhZVfBY', {
    api_host: 'https://eu.i.posthog.com',
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    disable_session_recording: true,
    // Autocapture OFF — and this one matters more than it looks.
    //
    // It defaults to ON, and what it records for every tap includes $el_text:
    // the visible text of whatever you touched. On a calendar that text IS the
    // private part. Tapping the card for "Visita oncologia" sent the words
    // "Visita oncologia" to the analytics; tapping the address opened a maps
    // link whose href carried the place.
    //
    // Every deliberate event we send was written to avoid exactly this — see
    // the note in utils/analytics.js, "never send personal content" — and the
    // automatic layer quietly undid it. The named events (event_created,
    // voice_used, signup…) carry the ACTION and nothing else, which is all the
    // product questions actually need. Nothing of value is lost here.
    autocapture: false,
  });
}

// Catch faults that escape React — plain scripts and rejected promises — so a
// tester who hits one does not have to think to write in about it. Installed
// before anything else runs, so it also sees what breaks during startup.
installErrorReporting();

// Accessibility: apply the saved "large text" preference ASAP (device-specific,
// stored in localStorage). Zoom keeps the fluid layout reflowing, so text grows
// without horizontal panning. See SettingsPage toggle.
try {
  const bt = localStorage.getItem('memo_bigtext');
  if (bt === '1') document.documentElement.style.zoom = '1.35';
  else if (bt === '2') document.documentElement.style.zoom = '1.6';
} catch (_) {}

// ── How much of the screen the keyboard is covering ─────────────────────────
//
// Two attempts failed before this one, and both failed for the same reason:
// they asked ANDROID to make the window smaller. interactive-widget=
// resizes-content asks the browser; android:windowSoftInputMode="adjustResize"
// asks the activity. Inside the installed app neither happens — on a modern,
// edge-to-edge Android the window keeps its full height and the keyboard is
// simply drawn on top. So the search box stayed under the keys: "cerca non si
// alza il campo", after a whole bundle spent on it.
//
// This asks nobody. visualViewport reports the part of the page you can
// actually SEE, so the difference between that and the window is exactly what
// the keyboard is hiding. It is a number the page can read for itself, which
// means it works in Chrome and in the WebView alike — and arrives by reloading,
// not by building.
//
//   --kb    the covered height, 0 when the keyboard is down
//   --navh  the bottom bar's height, or 0 while it is behind the keyboard
//
try {
  const vv = window.visualViewport;
  if (vv) {
    const root = document.documentElement;
    const measure = () => {
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // A few pixels of rounding are not a keyboard.
      const kb = covered > 40 ? Math.round(covered) : 0;
      root.style.setProperty('--kb', kb + 'px');
      root.style.setProperty('--navh', kb ? '0px' : '64px');
    };
    measure();
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
  }
} catch (_) {}

// Custom accent colour (device-specific) applied ASAP to avoid a flash.
try {
  const accent = localStorage.getItem('memo_accent');
  if (accent) applyAccent(accent);
} catch (_) {}

// Accessibility mode: high-contrast theme (class on <body>; its vars override
// the accent). Big text + accessible reminders are stored separately and
// applied by their own paths.
try {
  if (localStorage.getItem('memo_a11y_mode') === '1') document.body.classList.add('a11y-contrast');
} catch (_) {}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<React.StrictMode><App /></React.StrictMode>);
